// ActivityFeed — recent design events (renamed, pinned, ordered, etc.).
// Used on the Home view per spec §11.5 and inside the DetailPanel for a
// per-design timeline.
//
// Each row is clickable when there's an associated design — opens that
// design in the side drawer.

import { useQuery } from '@tanstack/react-query';
import {
  Pencil,
  Star,
  StarOff,
  Archive,
  RotateCcw,
  ShoppingCart,
  Folder as FolderIcon,
  ImageIcon,
  PlusCircle,
  Copy,
  Trash2,
  Clock,
} from 'lucide-react';
import { listActivity } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useStudio } from '../lib/store';
import { fmtRelative } from '../lib/format';
import type { ActivityEvent, ActivityEventType } from '../lib/types';

const ICONS: Record<ActivityEventType, typeof Pencil> = {
  created: PlusCircle,
  forked: Copy,
  reordered: ShoppingCart,
  pinned: Star,
  unpinned: StarOff,
  archived: Archive,
  restored: RotateCcw,
  renamed: Pencil,
  status_changed: Clock,
  agent_swapped: Pencil,
  moved_to_folder: FolderIcon,
  thumbnail_snapshotted: ImageIcon,
  deleted: Trash2,
};

function describe(e: ActivityEvent): string {
  const name = e.design?.name ?? 'a design';
  switch (e.event_type) {
    case 'created':
      return `Added ${name}`;
    case 'forked':
      return `Made an editable copy of ${name}`;
    case 'reordered': {
      const qty = (e.payload?.quantity as number | undefined) ?? null;
      return qty ? `Reordered ${name} (${qty} qty)` : `Reordered ${name}`;
    }
    case 'pinned':
      return `Pinned ${name}`;
    case 'unpinned':
      return `Unpinned ${name}`;
    case 'archived':
      return `Archived ${name}`;
    case 'restored':
      return `Restored ${name}`;
    case 'renamed': {
      const to = (e.payload?.to as string | null | undefined) ?? null;
      return to ? `Renamed to "${to}"` : `Renamed ${name}`;
    }
    case 'status_changed': {
      const to = (e.payload?.to as string | undefined) ?? '';
      return to ? `${name} is now ${to.replace(/_/g, ' ')}` : `${name} status changed`;
    }
    case 'moved_to_folder':
      return e.payload?.folder_id ? `Moved ${name} to a folder` : `Removed ${name} from folder`;
    case 'thumbnail_snapshotted':
      return `Saved a fresh preview of ${name}`;
    case 'deleted':
      return `Deleted ${name}`;
    case 'agent_swapped':
      return `Switched agent on ${name}`;
    default:
      return name;
  }
}

interface ActivityFeedProps {
  limit?: number;
  designId?: string;
  /** When true, omit the section header (used inside DetailPanel). */
  compact?: boolean;
}

export function ActivityFeed({ limit = 12, designId, compact = false }: ActivityFeedProps) {
  const openDetail = useStudio((s) => s.openDetail);
  const q = useQuery({
    queryKey: [...qk.activity, { limit, designId }],
    queryFn: () => listActivity({ limit, designId }),
    staleTime: 30_000,
  });

  if (q.isLoading) {
    return <div className="activity-feed__loading">Loading recent activity…</div>;
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="activity-feed__empty">
        Nothing here yet — your design activity will show up as you work.
      </div>
    );
  }

  return (
    <div className={`activity-feed${compact ? ' activity-feed--compact' : ''}`}>
      {!compact && <h3 className="activity-feed__title">Recent activity</h3>}
      <ul className="activity-feed__list" role="list">
        {items.map((e) => {
          const Icon = ICONS[e.event_type] ?? Pencil;
          const clickable = !!e.design_id;
          return (
            <li key={e.id} className="activity-feed__item">
              <button
                type="button"
                className="activity-feed__row"
                onClick={() => {
                  if (clickable && e.design_id) openDetail(e.design_id);
                }}
                disabled={!clickable}
              >
                {e.design?.thumbnail_url ? (
                  <img
                    src={e.design.thumbnail_url}
                    alt=""
                    aria-hidden="true"
                    className="activity-feed__thumb"
                  />
                ) : (
                  <div className="activity-feed__thumb activity-feed__thumb--icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                )}
                <div className="activity-feed__text">
                  <div className="activity-feed__msg">{describe(e)}</div>
                  <div className="activity-feed__when">{fmtRelative(e.created_at)}</div>
                </div>
                <Icon
                  className="activity-feed__type-icon"
                  size={16}
                  strokeWidth={1.6}
                  aria-hidden="true"
                />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
